import { DateTime } from 'luxon';
import {
    ReportCustomTemplateField,
    ReportCustomTemplateFieldTypes,
    ReportCustomTemplateFieldWithName,
    TemplateFieldDataTypes,
} from '../../api/dtos';
import GridActions from '../../components/GridActions';
import type { AppColumn, AppScalarType, AppScalarValue } from '../../components/tables/AppTable.types';

/** Dynamic report rows are raw scalar records returned by the report endpoints. */
export type ReportColumnRow = Record<string, AppScalarValue> & {
    id?: string | number;
    Id?: string | number;
};

const customNumericFieldTypes = new Set<ReportCustomTemplateFieldTypes>([
    ReportCustomTemplateFieldTypes.ContractCommission,
    ReportCustomTemplateFieldTypes.ContractDeposit,
    ReportCustomTemplateFieldTypes.ContractLegalFees,
    ReportCustomTemplateFieldTypes.ContractRateAdjustments,
    ReportCustomTemplateFieldTypes.ContractRebate,
    ReportCustomTemplateFieldTypes.ContractGst,
    ReportCustomTemplateFieldTypes.ContractPrice,
    ReportCustomTemplateFieldTypes.ContractWaterAdjustments,
    ReportCustomTemplateFieldTypes.LotAdvertisedPrice,
    ReportCustomTemplateFieldTypes.LotApprovedRebate,
    ReportCustomTemplateFieldTypes.LotLandValue,
    ReportCustomTemplateFieldTypes.ProjectCostPerLot,
    ReportCustomTemplateFieldTypes.ProjectDefaultCommission,
    ReportCustomTemplateFieldTypes.ProjectPercentShare,
    ReportCustomTemplateFieldTypes.ProjectValuation,
    ReportCustomTemplateFieldTypes.StageArea,
    ReportCustomTemplateFieldTypes.StageBudget,
    ReportCustomTemplateFieldTypes.StageCostPerLot,
]);

const customDateFieldTypes = new Set<ReportCustomTemplateFieldTypes>([
    ReportCustomTemplateFieldTypes.ContractActualSettleDate,
    ReportCustomTemplateFieldTypes.ContractDateBackOnMarket,
    ReportCustomTemplateFieldTypes.ContractExchangeDate,
    ReportCustomTemplateFieldTypes.ContractExpectedSettleDate,
    ReportCustomTemplateFieldTypes.ContractIssueDate,
    ReportCustomTemplateFieldTypes.LotDateOnMarket,
    ReportCustomTemplateFieldTypes.LotLandValueDate,
    ReportCustomTemplateFieldTypes.ProjectValuationDate,
    ReportCustomTemplateFieldTypes.StageRegistrationDate,
]);

function customReportNumber(value: AppScalarValue): AppScalarValue {
    if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
        return value;
    }

    const number = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN;

    return Number.isFinite(number) ? Math.round(number * 100) / 100 : value;
}

function customReportDate(value: AppScalarValue): AppScalarValue {
    if (value === null || value === undefined || value === '') {
        return value;
    }

    if (value instanceof Date) {
        return reportDate(value);
    }

    if (typeof value !== 'string') {
        return value;
    }

    const parsedDate = DateTime.fromFormat(value, 'dd/MM/yyyy');
    const date = parsedDate.isValid
        ? parsedDate
        : DateTime.fromISO(value, {
              setZone: true,
          });

    return date.isValid ? (date.toISODate() ?? value) : value;
}

function customReportBoolean(value: AppScalarValue): AppScalarValue {
    if (typeof value !== 'string') {
        return value;
    }

    switch (value.trim().toLowerCase()) {
        case 'true':
            return true;
        case 'false':
            return false;
        default:
            return value;
    }
}

function customReportScalarType(field?: ReportCustomTemplateField): AppScalarType {
    if(!field) {
        return 'text';
    }

    switch (field.dataType) {
        case TemplateFieldDataTypes.String:
            return 'text';
        case TemplateFieldDataTypes.Number:
            return 'number';
        case TemplateFieldDataTypes.Date:
            return 'date';
        case TemplateFieldDataTypes.Boolean:
            return 'boolean';
        case TemplateFieldDataTypes.Lookup:
            return 'lookup';
    }

    if (field.fieldType !== undefined && field.fieldType !== null) {
        if (customNumericFieldTypes.has(field.fieldType)) {
            return 'number';
        }

        if (customDateFieldTypes.has(field.fieldType)) {
            return 'date';
        }
    }

    return 'text';
}

/** Builds the selected custom-report schema in the metadata service's order. */

export const buildTemplateFieldColumns = (
    fields: ReportCustomTemplateField[] = [],
    fieldsWithNames: ReportCustomTemplateFieldWithName[] = [],
): AppColumn<ReportColumnRow>[] =>
    fieldsWithNames
        .flatMap((namedField) => {
            const selected = fields.find(
                (field) =>
                    field.fieldType === namedField.field.fieldType &&
                    field.fieldReferenceId === namedField.field.fieldReferenceId,
            );

            return selected
                ? [
                      {
                          ...namedField,
                          selected,
                      },
                  ]
                : [];
        })
        .map(({ field, name, selected }) => {
            const fieldType = field.fieldType;
            const isProperty = fieldType === ReportCustomTemplateFieldTypes.LotProperty;
            const enumName =
                fieldType !== null && fieldType !== undefined ? ReportCustomTemplateFieldTypes[fieldType] : undefined;
            const specifier = isProperty ? name : typeof enumName === 'string' ? enumName : '';
            const typePart = fieldType === null || fieldType === undefined ? 'null' : String(fieldType);
            const id = `custom:${typePart}:${
                field.fieldReferenceId === null || field.fieldReferenceId === undefined
                    ? `field-${field.id ?? 'none'}`
                    : `ref-${field.fieldReferenceId}`
            }`;
            const scalarType = customReportScalarType(selected);
            const isNumeric = scalarType === 'number';
            const isDate = scalarType === 'date';

            return {
                id,
                label: name,
                accessor: (row) =>
                    isNumeric
                        ? customReportNumber(row[specifier])
                        : isDate
                          ? customReportDate(row[specifier])
                          : scalarType === 'boolean'
                            ? customReportBoolean(row[specifier])
                            : row[specifier],
                scalarType,
                formatter: (value) => (isDate ? reportDateDisplay(value) || String(value ?? '') : String(value ?? '')),
                flex: 1,
                minWidth: 100,
            };
        });

/** App-owned asset columns; route base is supplied by the eventual consumer. */
export const buildAssetsColumns = (formUrlBase: string): AppColumn<ReportColumnRow>[] => [
    {
        id: 'Name',
        label: 'Asset Name',
        accessor: (row) => row.Name,
        scalarType: 'text',
        flex: 1,
    },
    {
        id: 'Address',
        label: 'Address',
        accessor: (row) => row.Address,
        scalarType: 'text',
        flex: 1,
    },
    {
        id: 'BankValuation',
        label: 'Bank Valuation',
        accessor: (row) => reportNumber(row.BankValuation),
        scalarType: 'number',
        align: 'left',
        flex: 1,
        formatter: (value) => reportMoneyDisplay(value),
        aggregateFormatter: (value) => audCurrency.format(value),
        aggregate: 'sum',
    },
    {
        id: 'BankValuationDate',
        label: 'Bank Valuation Date',
        accessor: (row) => reportDate(row.BankValuationDate),
        scalarType: 'date',
        formatter: (value) => reportDateDisplay(value),
        flex: 1,
    },
    {
        id: 'DirectorsValuation',
        label: 'Directors Valuation',
        accessor: (row) => reportNumber(row.DirectorsValuation),
        scalarType: 'number',
        align: 'left',
        flex: 1,
        formatter: (value) => reportMoneyDisplay(value),
        aggregateFormatter: (value) => audCurrency.format(value),
        aggregate: 'sum',
    },
    {
        id: 'DirectorsValuationDate',
        label: 'Directors Valuation Date',
        accessor: (row) => reportDate(row.DirectorsValuationDate),
        scalarType: 'date',
        formatter: (value) => reportDateDisplay(value),
        flex: 1,
    },
    {
        id: 'LoanAccountId',
        label: 'Loan Account ID',
        accessor: (row) => row.LoanAccountId,
        scalarType: 'text',
        flex: 1,
    },
    {
        id: 'actions',
        label: '',
        headerLabel: '',
        accessor: () => null,
        scalarType: 'text',
        kind: 'action',
        filterable: false,
        sortable: false,
        groupable: false,
        exportable: false,
        cell: ({ row }) => {
            const assetId = row.id ?? row.Id;

            return assetId === null || assetId === undefined ? null : (
                <GridActions
                    editUrl={`/${formUrlBase}/assets/edit/${assetId}`}
                    deleteUrl={`/${formUrlBase}/assets/delete/${assetId}`}
                />
            );
        },
    },
];

const audCurrency = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

function reportNumber(value: AppScalarValue): AppScalarValue {
    if (value === null || value === undefined || typeof value === 'number') {
        return value;
    }

    if (typeof value !== 'string' || value.trim() === '') {
        return value;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : value;
}

function reportDateDisplay(value: AppScalarValue): string {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    const date =
        value instanceof Date
            ? DateTime.fromJSDate(value)
            : typeof value === 'string'
              ? DateTime.fromISO(value)
              : undefined;

    return date?.isValid ? date.toFormat('dd/MM/yyyy') : '';
}

function reportDate(value: AppScalarValue): AppScalarValue {
    if (value === null || value === undefined || value === '') {
        return value;
    }

    const date =
        value instanceof Date
            ? DateTime.fromJSDate(value)
            : typeof value === 'string'
              ? DateTime.fromISO(value)
              : undefined;

    return date?.isValid ? (date.toISODate() ?? value) : value;
}

function reportMoneyDisplay(value: AppScalarValue): string {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    return typeof value === 'number' ? audCurrency.format(value) : String(value);
}

export const buildLotSummaryColumns = (): AppColumn<ReportColumnRow>[] => [
    {
        id: 'Field',
        label: 'Field',
        headerLabel: '',
        accessor: (row) => row.Field,
        scalarType: 'text',
        flex: 1,
    },
    {
        id: 'Value',
        label: 'Value',
        headerLabel: '',
        accessor: (row) => row.Value,
        scalarType: 'text',
        flex: 1,
    },
];

export const buildContractCancellationSummaryColumns = (): AppColumn<ReportColumnRow>[] => [
    {
        id: 'Month',
        label: 'Month',
        accessor: (row) => row.Month,
        scalarType: 'text',
        flex: 1,
    },
    {
        id: 'ContractsCancelled',
        label: 'Contracts Cancelled',
        accessor: (row) => reportNumber(row.ContractsCancelled),
        scalarType: 'number',
        align: 'left',
        flex: 1,
        aggregate: 'sum',
    },
    {
        id: 'ContractRecissions',
        label: 'Contract Recissions',
        accessor: (row) => reportNumber(row.ContractRecissions),
        scalarType: 'number',
        align: 'left',
        flex: 1,
        aggregate: 'sum',
    },
    {
        id: 'ContractsForfeited',
        label: 'Contracts Forfeited',
        accessor: (row) => reportNumber(row.ContractsForfeited),
        scalarType: 'number',
        align: 'left',
        flex: 1,
        aggregate: 'sum',
    },
];

export const buildContractsOverdueColumns = (): AppColumn<ReportColumnRow>[] => [
    {
        id: 'ProjectName',
        label: 'Project',
        accessor: (row) => row.ProjectName,
        scalarType: 'text',
        flex: 1,
    },
    {
        id: 'StageName',
        label: 'Stage',
        accessor: (row) => row.StageName,
        scalarType: 'text',
        flex: 1,
    },
    {
        id: 'LotNumber',
        label: 'Lot',
        accessor: (row) => row.LotNumber,
        scalarType: 'text',
        flex: 1,
    },
    {
        id: 'OverdueType',
        label: 'Overdue',
        accessor: (row) => row.OverdueType,
        scalarType: 'text',
        flex: 1,
    },
    {
        id: 'ContractPrice',
        label: 'Contract Price',
        accessor: (row) => reportNumber(row.ContractPrice),
        scalarType: 'number',
        align: 'left',
        flex: 1,
        formatter: (value) => reportMoneyDisplay(value),
        aggregateFormatter: (value) => audCurrency.format(value),
        aggregate: 'sum',
    },
    {
        id: 'IssueDate',
        label: 'Issue Date',
        accessor: (row) => reportDate(row.IssueDate),
        scalarType: 'date',
        formatter: (value) => reportDateDisplay(value),
        flex: 1,
    },
    {
        id: 'DueDate',
        label: 'Due Date',
        accessor: (row) => reportDate(row.DueDate),
        scalarType: 'date',
        formatter: (value) => reportDateDisplay(value),
        flex: 1,
    },
    {
        id: 'DaysOverdue',
        label: 'Days Overdue',
        accessor: (row) => reportNumber(row.DaysOverdue),
        scalarType: 'number',
        align: 'left',
        flex: 1,
    },
];

export const buildUpcomingSettlementsColumns = (): AppColumn<ReportColumnRow>[] => [
    {
        id: 'ProjectName',
        label: 'Project',
        accessor: (row) => row.ProjectName,
        scalarType: 'text',
        flex: 1,
    },
    {
        id: 'StageName',
        label: 'Stage',
        accessor: (row) => row.StageName,
        scalarType: 'text',
        flex: 1,
    },
    {
        id: 'LotNumber',
        label: 'Lot',
        accessor: (row) => row.LotNumber,
        scalarType: 'text',
        flex: 1,
    },
    {
        id: 'Amount',
        label: 'Amount',
        accessor: (row) => reportNumber(row.Amount),
        scalarType: 'number',
        align: 'left',
        flex: 1,
        formatter: (value) => reportMoneyDisplay(value),
        aggregateFormatter: (value) => audCurrency.format(value),
        aggregate: 'sum',
    },
    {
        id: 'ExpectedSettleDate',
        label: 'Date',
        accessor: (row) => reportDate(row.ExpectedSettleDate),
        scalarType: 'date',
        formatter: (value) => reportDateDisplay(value),
        flex: 1,
    },
    {
        id: 'AgentName',
        label: 'Agent',
        accessor: (row) => row.AgentName,
        scalarType: 'text',
        flex: 1,
    },
];

export const buildLotsSettledByFinancialYearColumns = (): AppColumn<ReportColumnRow>[] => [
    { id: 'ProjectName', label: 'Project', accessor: row => row.ProjectName, scalarType: 'lookup', flex: 1 },
    { id: 'FinancialYear', label: 'Financial Year', accessor: row => reportNumber(row.FinancialYear),
        scalarType: 'number', formatter: value => `FY${value}`, flex: 1 },
    { id: 'QuarterEnd', label: 'Quarter Ending', accessor: row => reportDate(row.QuarterEnd),
        scalarType: 'date', formatter: value => reportDateDisplay(value), flex: 1 },
    { id: 'SettledBeforeFinancialYear', label: 'Settled Before Financial Year',
        accessor: row => reportNumber(row.SettledBeforeFinancialYear), scalarType: 'number', aggregate: 'sum', flex: 1 },
    { id: 'SettledFinancialYearToQuarterEnd', label: 'Settled FY to Quarter End',
        accessor: row => reportNumber(row.SettledFinancialYearToQuarterEnd), scalarType: 'number', aggregate: 'sum', flex: 1 },
];

export const buildSettlementDateSummaryColumns = (fields?: ReportCustomTemplateField[]): AppColumn<ReportColumnRow>[] => [
    {
        id: 'ProjectName',
        label: 'Project',
        accessor: (row) => row.ProjectName,
        scalarType: customReportScalarType(fields?.find(x => x.propertyName == 'ProjectName')) ?? 'text',
        flex: 1,
    },
    {
        id: 'StageName',
        label: 'Stage',
        accessor: (row) => row.StageName,
        scalarType: customReportScalarType(fields?.find(x => x.propertyName == 'StageName')) ?? 'text',
        flex: 1,
    },
    {
        id: 'LotNumber',
        label: 'Lot',
        accessor: (row) => row.LotNumber,
        scalarType: customReportScalarType(fields?.find(x => x.propertyName == 'LotNumber')) ?? 'text',
        aggregate: "count",
        flex: 1,
    },
    {
        id: 'LotStatus',
        label: 'Lot Status',
        accessor: (row) =>
            typeof row.LotStatus === 'string' ? row.LotStatus.replace(/([A-Z])/g, ' $1').trim() : row.LotStatus,
        scalarType: customReportScalarType(fields?.find(x => x.propertyName == 'LotStatus')) ?? 'text',
        flex: 1,
    },
    {
        id: 'ContractSettleDate',
        label: 'Settlement Date',
        accessor: (row) => reportDate(row.ContractSettleDate),
        scalarType: customReportScalarType(fields?.find(x => x.propertyName == 'ContractSettleDate')) ?? 'text',
        formatter: (value) => reportDateDisplay(value),
        flex: 1,
    },
    {
        id: 'ContractPrice',
        label: 'Contract Price',
        accessor: (row) => reportNumber(row.ContractPrice),
        scalarType: 'number',
        align: 'left',
        flex: 1,
        formatter: (value) => reportMoneyDisplay(value),
        aggregateFormatter: (value) => audCurrency.format(value),
        aggregate: 'sum',
    },
];

export const buildContractsExchangedSummaryColumns = (fields?: ReportCustomTemplateField[]): AppColumn<ReportColumnRow>[] =>
    buildSettlementDateSummaryColumns(fields).flatMap(column => column.id === 'ContractSettleDate'
        ? [
            {
                id: 'ContractExchangeDate',
                label: 'Contract Exchange Date',
                accessor: (row: ReportColumnRow) => reportDate(row.ContractExchangeDate),
                scalarType: 'date' as const,
                formatter: (value: AppScalarValue) => reportDateDisplay(value),
                flex: 1,
            },
            {
                id: 'ContractExpectedSettleDate',
                label: 'Expected Settlement Date',
                accessor: (row: ReportColumnRow) => reportDate(row.ContractExpectedSettleDate),
                scalarType: 'date' as const,
                formatter: (value: AppScalarValue) => reportDateDisplay(value),
                flex: 1,
            },
        ]
        : [column]);

export const buildCommissionReportColumns = (): AppColumn<ReportColumnRow>[] => [
    {
        id: 'LotNumber',
        label: 'Lot',
        accessor: (row) => row.LotNumber,
        scalarType: 'text',
        flex: 1,
    },
    {
        id: 'ContractPrice',
        label: 'Contract Price',
        accessor: (row) => reportNumber(row.ContractPrice),
        scalarType: 'number',
        align: 'left',
        flex: 1,
        formatter: (value) => reportMoneyDisplay(value),
        aggregateFormatter: (value) => audCurrency.format(value),
        aggregate: 'sum',
    },
    {
        id: 'ExchangeDate',
        label: 'Exchange Date',
        accessor: (row) => reportDate(row.ExchangeDate),
        scalarType: 'date',
        formatter: (value) => reportDateDisplay(value),
        flex: 1,
    },
    {
        id: 'SettleDate',
        label: 'Settle Date',
        accessor: (row) => reportDate(row.SettleDate),
        scalarType: 'date',
        formatter: (value) => reportDateDisplay(value),
        flex: 1,
    },
    {
        id: 'Commission',
        label: 'Commission',
        accessor: (row) => reportNumber(row.Commission),
        scalarType: 'number',
        align: 'left',
        flex: 1,
        formatter: (value) => reportMoneyDisplay(value),
        aggregateFormatter: (value) => audCurrency.format(value),
        aggregate: 'sum',
    },
    {
        id: 'CommissionGst',
        label: 'GST on Commission',
        accessor: (row) => reportNumber(row.CommissionGst),
        scalarType: 'number',
        align: 'left',
        flex: 1,
        formatter: (value) => reportMoneyDisplay(value),
        aggregateFormatter: (value) => audCurrency.format(value),
        aggregate: 'sum',
    },
    {
        id: 'TotalCommission',
        label: 'Total Commission',
        accessor: (row) => reportNumber(row.TotalCommission),
        scalarType: 'number',
        align: 'left',
        flex: 1,
        formatter: (value) => reportMoneyDisplay(value),
        aggregateFormatter: (value) => audCurrency.format(value),
        aggregate: 'sum',
    },
    {
        id: 'PaidOn',
        label: 'Paid On',
        accessor: (row) => row.PaidOn,
        scalarType: 'text',
        flex: 1,
    },
];

export const buildLotPricingComparisonColumns = (): AppColumn<ReportColumnRow>[] => [
    {
        id: 'ProjectName',
        label: 'Project',
        accessor: (row) => row.ProjectName,
        scalarType: 'text',
        flex: 1,
    },
    {
        id: 'StageName',
        label: 'Stage',
        accessor: (row) => row.StageName,
        scalarType: 'text',
        flex: 1,
    },
    {
        id: 'Substage',
        label: 'Substage',
        accessor: (row) => row.Substage,
        scalarType: 'text',
        flex: 1,
    },
    {
        id: 'NumLots',
        label: 'No. of Lots',
        accessor: (row) => reportNumber(row.NumLots),
        scalarType: 'number',
        align: 'left',
        flex: 1,
        aggregate: 'sum',
    },
    {
        id: 'PriceLeader',
        label: 'Price Leader',
        accessor: (row) => reportNumber(row.PriceLeader),
        scalarType: 'number',
        align: 'left',
        flex: 1,
        formatter: (value) => reportMoneyDisplay(value),
        aggregateFormatter: (value) => audCurrency.format(value),
    },
    {
        id: 'AvgLotPrice',
        label: 'Avg Lot Price',
        accessor: (row) => reportNumber(row.AvgLotPrice),
        scalarType: 'number',
        align: 'left',
        flex: 1,
        formatter: (value) => reportMoneyDisplay(value),
        aggregateFormatter: (value) => audCurrency.format(value),
    },
    {
        id: 'AvgPricePerMeterSquared',
        label: ' Avg $/m2',
        accessor: (row) => reportNumber(row.AvgPricePerMeterSquared),
        scalarType: 'number',
        align: 'left',
        flex: 1,
        formatter: (value) => reportMoneyDisplay(value),
        aggregateFormatter: (value) => audCurrency.format(value),
    },
    {
        id: 'TotalRevenue',
        label: 'Total Revenue',
        accessor: (row) => reportNumber(row.TotalRevenue),
        scalarType: 'number',
        align: 'left',
        flex: 1,
        formatter: (value) => reportMoneyDisplay(value),
        aggregateFormatter: (value) => audCurrency.format(value),
        aggregate: 'sum',
    },
];

function reportDynamicKeys(rows: readonly ReportColumnRow[], excluded: ReadonlySet<string>): string[] {
    const seen = new Set<string>();
    const keys: string[] = [];

    for (const row of rows) {
        for (const key of Object.keys(row)) {
            if (key !== 'id' && key !== 'Id' && !excluded.has(key) && !seen.has(key)) {
                seen.add(key);
                keys.push(key);
            }
        }
    }

    return keys;
}

export const buildSalesSummaryColumns = (rows: readonly ReportColumnRow[]): AppColumn<ReportColumnRow>[] => {
    const fixedIds = new Set(['Month', 'LotsExchanged', 'TotalContractValue', 'AverageLotPrice']);

    return [
        {
            id: 'Month',
            label: 'Month',
            accessor: (row) => row.Month,
            scalarType: 'text',
            flex: 1,
        },
        {
            id: 'LotsExchanged',
            label: 'No. of Standard Residential Lots Exchanged',
            accessor: (row) => reportNumber(row.LotsExchanged),
            scalarType: 'number',
            align: 'left',
            flex: 1,
            aggregate: 'sum',
        },
        {
            id: 'TotalContractValue',
            label: 'Total Contract Value',
            accessor: (row) => reportNumber(row.TotalContractValue),
            scalarType: 'number',
            align: 'left',
            flex: 1,
            formatter: (value) => reportMoneyDisplay(value),
            aggregateFormatter: (value) => audCurrency.format(value),
            aggregate: 'sum',
        },
        {
            id: 'AverageLotPrice',
            label: 'Average Price / Lot',
            accessor: (row) => reportNumber(row.AverageLotPrice),
            scalarType: 'number',
            align: 'left',
            flex: 1,
            formatter: (value) => reportMoneyDisplay(value),
            aggregateFormatter: (value) => audCurrency.format(value),
        },
        ...reportDynamicKeys(rows, fixedIds).map((key) => ({
            id: key,
            label: key,
            accessor: (row: ReportColumnRow) => row[key],
            scalarType: 'text' as const,
            flex: 1,
        })),
    ];
};

export const buildGrossRealisationColumns = (rows: readonly ReportColumnRow[]): AppColumn<ReportColumnRow>[] => {
    const fixedIds = new Set([
        'ProjectId',
        'ProjectName',
        'ShortAverage',
        'ShortIncrease',
        'MediumAverage',
        'MediumIncrease',
        'LongAverage',
        'LongIncrease',
    ]);
    const shortGroup = {
        id: 'gross-short',
        label: 'Short (1 year)',
    };
    const mediumGroup = {
        id: 'gross-medium',
        label: 'Medium (3 years)',
    };
    const longGroup = {
        id: 'gross-long',
        label: 'Long (Project)',
    };

    return [
        {
            id: 'ProjectName',
            label: 'Project Name',
            accessor: (row) => row.ProjectName,
            scalarType: 'text',
            minWidth: 300,
        },
        ...reportDynamicKeys(rows, fixedIds)
            .sort((a, b) => (a < b ? -1 : 1))
            .map((key) => ({
                id: key,
                label: key,
                accessor: (row: ReportColumnRow) => row[key],
                scalarType: 'text' as const,
                minWidth: 120,
            })),
        {
            id: 'ShortAverage',
            label: 'Short Average',
            headerLabel: '',
            accessor: (row) => reportNumber(row.ShortAverage),
            scalarType: 'number',
            align: 'left',
            minWidth: 100,
            formatter: (value) => reportMoneyDisplay(value),
            aggregateFormatter: (value) => audCurrency.format(value),
            groupHeader: shortGroup,
        },
        {
            id: 'ShortIncrease',
            label: 'Short Increase',
            headerLabel: '% Increase',
            accessor: (row) => reportNumber(row.ShortIncrease),
            scalarType: 'number',
            align: 'left',
            minWidth: 100,
            groupHeader: shortGroup,
        },
        {
            id: 'MediumAverage',
            label: 'Medium Average',
            headerLabel: 'Average',
            accessor: (row) => reportNumber(row.MediumAverage),
            scalarType: 'number',
            align: 'left',
            minWidth: 100,
            formatter: (value) => reportMoneyDisplay(value),
            aggregateFormatter: (value) => audCurrency.format(value),
            groupHeader: mediumGroup,
        },
        {
            id: 'MediumIncrease',
            label: 'Medium Increase',
            headerLabel: '% Increase',
            accessor: (row) => reportNumber(row.MediumIncrease),
            scalarType: 'number',
            align: 'left',
            minWidth: 100,
            groupHeader: mediumGroup,
        },
        {
            id: 'LongAverage',
            label: 'Long Average',
            headerLabel: 'Average',
            accessor: (row) => reportNumber(row.LongAverage),
            scalarType: 'number',
            align: 'left',
            minWidth: 100,
            formatter: (value) => reportMoneyDisplay(value),
            aggregateFormatter: (value) => audCurrency.format(value),
            groupHeader: longGroup,
        },
        {
            id: 'LongIncrease',
            label: 'Long Increase',
            headerLabel: '% Increase',
            accessor: (row) => reportNumber(row.LongIncrease),
            scalarType: 'number',
            align: 'left',
            minWidth: 100,
            groupHeader: longGroup,
        },
    ];
};

export const buildSalesRateColumns = (rows: readonly ReportColumnRow[]): AppColumn<ReportColumnRow>[] => {
    const fixedIds = new Set([
        'ProjectId',
        'ProjectName',
        'ShortMonth',
        'ShortPa',
        'MediumMonth',
        'MediumPa',
        'LongMonth',
        'LongPa',
    ]);
    const shortGroup = {
        id: 'sales-rate-short',
        label: 'Short (1 year)',
    };
    const mediumGroup = {
        id: 'sales-rate-medium',
        label: 'Medium (3 years)',
    };
    const longGroup = {
        id: 'sales-rate-long',
        label: 'Long (Project)',
    };

    return [
        {
            id: 'ProjectName',
            label: 'Project Name',
            accessor: (row) => row.ProjectName,
            scalarType: 'text',
            minWidth: 300,
        },
        ...reportDynamicKeys(rows, fixedIds)
            .sort((a, b) => (a < b ? -1 : 1))
            .map((key) => ({
                id: key,
                label: key,
                accessor: (row: ReportColumnRow) => row[key],
                scalarType: 'text' as const,
                minWidth: 120,
            })),
        {
            id: 'ShortPa',
            label: 'Short Annual',
            headerLabel: 'Annual',
            accessor: (row) => reportNumber(row.ShortPa),
            scalarType: 'number',
            align: 'left',
            minWidth: 100,
            groupHeader: shortGroup,
        },
        {
            id: 'ShortMonth',
            label: 'Short Monthly',
            headerLabel: 'Monthly',
            accessor: (row) => reportNumber(row.ShortMonth),
            scalarType: 'number',
            align: 'left',
            minWidth: 100,
            groupHeader: shortGroup,
        },
        {
            id: 'MediumPa',
            label: 'Medium Annual',
            headerLabel: 'Annual',
            accessor: (row) => reportNumber(row.MediumPa),
            scalarType: 'number',
            align: 'left',
            minWidth: 100,
            groupHeader: mediumGroup,
        },
        {
            id: 'MediumMonth',
            label: 'Medium Monthly',
            headerLabel: 'Monthly',
            accessor: (row) => reportNumber(row.MediumMonth),
            scalarType: 'number',
            align: 'left',
            minWidth: 100,
            groupHeader: mediumGroup,
        },
        {
            id: 'LongPa',
            label: 'Long Annual',
            headerLabel: 'Annual',
            accessor: (row) => reportNumber(row.LongPa),
            scalarType: 'number',
            align: 'left',
            minWidth: 100,
            groupHeader: longGroup,
        },
        {
            id: 'LongMonth',
            label: 'Long Monthly',
            headerLabel: 'Monthly',
            accessor: (row) => reportNumber(row.LongMonth),
            scalarType: 'number',
            align: 'left',
            minWidth: 100,
            groupHeader: longGroup,
        },
    ];
};

export const buildSalesAdviceToContractColumns = (): AppColumn<ReportColumnRow>[] => [
    {
        id: 'ProjectName',
        label: 'Project',
        accessor: (row) => row.ProjectName,
        scalarType: 'text',
        flex: 1,
    },
    {
        id: 'AverageDays',
        label: 'Average Days',
        accessor: (row) => reportNumber(row.AverageDays),
        scalarType: 'number',
        align: 'left',
        flex: 1,
    },
];

export const buildStageLotCountsColumns = (): AppColumn<ReportColumnRow>[] => [
    {
        id: 'PrecinctName',
        label: 'Precinct',
        accessor: (row) => row.PrecinctName,
        scalarType: 'text',
        flex: 1,
    },
    {
        id: 'StageName',
        label: 'Stage Name',
        accessor: (row) => row.StageName,
        scalarType: 'text',
        flex: 1,
    },
    {
        id: 'DaApprovedLots',
        label: 'DA Approved Residential Lots',
        accessor: (row) => reportNumber(row.DaApprovedLots),
        scalarType: 'number',
        align: 'left',
        flex: 1,
        aggregate: 'sum',
    },
    {
        id: 'ResidentialSites',
        label: 'Residential Development Sites',
        accessor: (row) => reportNumber(row.ResidentialSites),
        scalarType: 'number',
        align: 'left',
        flex: 1,
        aggregate: 'sum',
    },
    {
        id: 'ReferStage',
        label: 'Potential Lot Yield',
        accessor: (row) => row.ReferStage,
        scalarType: 'text',
        flex: 1,
    },
    {
        id: 'SpecialLots',
        label: 'Special Lots',
        accessor: (row) => reportNumber(row.SpecialLots),
        scalarType: 'number',
        align: 'left',
        flex: 1,
        aggregate: 'sum',
    },
    {
        id: 'Comments',
        label: 'Comments',
        accessor: (row) => row.Comments,
        scalarType: 'text',
        flex: 1,
    },
];
